import { phonePipe } from '../../pipes/phone-pipe';
import { Component, Inject } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { PDFService } from '../../services/pdf.service';
import { ConnectionService } from '../../services/connection.service';
import { Task } from '@contracts/interfaces/task.interface';
import { TaskService } from '../../services/task.service';
import { RunSheetService } from '../../services/run-sheet.service';

interface RunSheetTaskGroup {
  id: string;
  name: string;
}

interface RunSheetPrintGroup {
  name: string;
  tasks: any[];
}

interface RunSheetTaskSection extends RunSheetTaskGroup {
  tasks: Task[];
}

@Component({
  selector: 'ap-run-sheet-dialog',
  templateUrl: 'run-sheet.dialog.html',
  styleUrls: ['./run-sheet.dialog.scss', '../common-dialog-styles.scss']
})
export class RunSheetDialogComponent {

  pdfSrc: string = null;
  htmlSrc: string = null;

  tasks: Task[] = [];
  taskGroups: RunSheetTaskGroup[] = [];
  groupedTaskSections: RunSheetTaskSection[] = [];
  taskGroupAssignments: {[taskId: string]: string} = {};
  newGroupName = '';
  private runSheetLoaded = false;

  constructor(
      @Inject(MAT_DIALOG_DATA) public data: any,
      public dialogRef: MatDialogRef<RunSheetDialogComponent>,
      public dialog: MatDialog,
      public pdfService: PDFService,
      public connectionService: ConnectionService,
      public taskService: TaskService,
      public runSheetService: RunSheetService) {
      }

      ngOnInit(): void {
        this.runSheetService.getDefault().subscribe((runSheet: any) => {
          this.taskGroups = runSheet?.groups || [];
          this.taskGroupAssignments = runSheet?.taskGroupAssignments || {};
          this.runSheetLoaded = true;
          this.rebuildGroupedTaskSections();
        });

        this.taskService.getActive().subscribe((tasks: Task[]) => {

          this.tasks = (tasks || []).map(task => {
            if (!task.log) {
              task.log = [];
            }

            task.log.push({
              date: new Date(),
              text: "",
              type: "comment"
            });

            return task;
          });

          this.rebuildGroupedTaskSections();
        });
      }

      addGroup() {
        const name = this.newGroupName.trim();

        if (!name) {
          return;
        }

        const existing = this.taskGroups.find(group => group.name.toLowerCase() === name.toLowerCase());
        if (existing) {
          this.newGroupName = '';
          return;
        }

        this.taskGroups.push({
          id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
          name
        });
        this.newGroupName = '';
        this.rebuildGroupedTaskSections();
        this.saveRunSheet();
      }

      clearGroups() {
        this.taskGroups = [];
        this.taskGroupAssignments = {};
        this.rebuildGroupedTaskSections();
        this.saveRunSheet();
      }

      removeGroup(group: RunSheetTaskGroup) {
        this.taskGroups = this.taskGroups.filter(item => item.id !== group.id);

        for (let taskId of Object.keys(this.taskGroupAssignments)) {
          if (this.taskGroupAssignments[taskId] === group.id) {
            delete this.taskGroupAssignments[taskId];
          }
        }

        this.rebuildGroupedTaskSections();
        this.saveRunSheet();
      }

      rebuildGroupedTaskSections() {
        const groups = this.taskGroups.map(group => ({
          ...group,
          tasks: this.tasks.filter(task => this.getTaskGroupId(task) === group.id)
        })).filter(group => group.tasks.length > 0);

        const ungrouped = this.tasks.filter(task => !this.getTaskGroupId(task));

        this.groupedTaskSections = [
          ...groups,
          {
            id: '',
            name: 'Ungrouped',
            tasks: ungrouped
          }
        ].filter(group => group.tasks.length > 0);
      }

      getTaskGroupId(task: Task): string {
        if (task?.id == null) {
          return '';
        }

        return this.taskGroupAssignments[String(task.id)] || '';
      }

      setTaskGroup(task: Task, groupId: string) {
        if (task?.id == null) {
          return;
        }

        if (groupId) {
          this.taskGroupAssignments[String(task.id)] = groupId;
        } else {
          delete this.taskGroupAssignments[String(task.id)];
        }

        this.rebuildGroupedTaskSections();
        this.saveRunSheet();
      }

      trackByGroup(index: number, group: RunSheetTaskGroup) {
        return group.id || 'ungrouped';
      }

      trackByTask(index: number, task: Task) {
        return task.id;
      }

      completeTask(task: Task) {

      }

      saveTask(task: Task, evt: FocusEvent) {
        const input = evt.target as HTMLInputElement;

        if (!task.log) {
          task.log = [];
        }

        if (task.log.length === 0) {
          task.log.push({
            date: new Date(),
            text: "",
            type: "comment"
          });
        }

        task.log[task.log.length-1].text = input.value;

        this.taskService.update(task).subscribe(r=> {});
      }

      getLastComment(task: Task) {
        if (task.log?.length > 1) {
          return task.log[task.log.length-2].text;
        } else {
          return "";
        }
      }

      getCurrentComment(task: Task) {
        if (task.log?.length > 0) {
          return task.log[task.log.length-1].text;
        } else {
          return "";
        }
      }

      recall () {
        let updatedTasks: Task[] = [];
        for (let task of this.tasks) {
          if (task.log.length > 1) {
            task.log[task.log.length - 1].text = task.log[task.log.length - 2].text
          }

          updatedTasks.push(task);
        }
        this.tasks = updatedTasks;
        this.rebuildGroupedTaskSections();
      }

      saveRunSheet() {
        if (!this.runSheetLoaded) {
          return;
        }

        const groupIds = new Set(this.taskGroups.map(group => group.id));
        const taskGroupAssignments = {};

        for (let taskId of Object.keys(this.taskGroupAssignments)) {
          const groupId = this.taskGroupAssignments[taskId];

          if (groupIds.has(groupId)) {
            taskGroupAssignments[taskId] = groupId;
          }
        }

        this.runSheetService.updateDefault({
          groups: this.taskGroups,
          taskGroupAssignments
        }).subscribe(() => {});
      }

      print() {

        // id
        // subject
        // last log comment
        let printGroups: RunSheetPrintGroup[] = [];

        for (let group of this.groupedTaskSections) {
          printGroups.push(this.buildPrintGroup(group));
        }

        this.pdfService.print("task-run-sheet", { groups: printGroups });

      }

      printGroup(group: RunSheetTaskSection) {
        this.pdfService.print("task-run-sheet", {
          groups: [
            this.buildPrintGroup(group)
          ]
        });
      }

      private buildPrintGroup(group: RunSheetTaskSection): RunSheetPrintGroup {
        return {
          name: group.name,
          tasks: group.tasks.map(task => ({
            id: task.id,
            subject: task.subject,
            comment: this.getLastComment(task),
            customer: task.customer?.name,
            contact: new phonePipe().transform(task.customer?.phone),
            random: Math.random()
          }))
        };
      }

      openTask(task: Task) {
        this.taskService.openTaskDialog(task, null, null, null);
      }
}
